import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import Holder from "lib/icons/Holder";

type Props = {
  id: string;
  name?: string | null;
  prompt: string;
  canDelete: boolean;
  // Holds the row still while a change is saving
  disabled?: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

export default function QuickPromptRow({
  id,
  name,
  prompt,
  canDelete,
  disabled = false,
  onEdit,
  onDelete,
}: Props): JSX.Element {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } =
    useSortable({ id, disabled });

  const deleteButton = (
    <Button
      icon="trash03"
      onClick={onDelete}
      aria-label="Delete"
      buttonType="tertiary"
      disabled={disabled || !canDelete}
      iconProps={{ color: "sysTextDefault" }}
    />
  );

  return (
    <Row
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      aria-label={name ?? prompt}
    >
      <Flex gap={8} alignItems="center" minWidth={0}>
        <DragHandle
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <Holder />
        </DragHandle>

        <Flex column gap={4} minWidth={0}>
          {name && <Text bold>{name}</Text>}
          <PromptText hasName={Boolean(name)}>{prompt}</PromptText>
        </Flex>
      </Flex>
      <ButtonWrapper>
        <Button
          icon="edit05"
          onClick={onEdit}
          aria-label="Edit"
          buttonType="tertiary"
          disabled={disabled}
          iconProps={{ color: "sysTextDefault" }}
        />
        {canDelete ? (
          deleteButton
        ) : (
          <Tooltip content="Your team needs at least one quick prompt">
            <span>{deleteButton}</span>
          </Tooltip>
        )}
      </ButtonWrapper>
    </Row>
  );
}

const Row = styled(Flex)(({ theme }) => ({
  padding: "16px 8px",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: 8,
  gap: 8,
  "&:hover": {
    backgroundColor: theme.colors.sysBackgroundAlternative,
  },
}));

// A long prompt shows its first two lines
const PromptText = styled(Text)<{ hasName: boolean }>(({ hasName, theme }) => ({
  color: hasName ? theme.colors.sysTextSecondary : theme.colors.sysTextDefault,
  fontWeight: hasName ? undefined : 600,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  whiteSpace: "pre-line",
}));

// Shows on hover or keyboard focus, and always on touch screens, which can't hover
const DragHandle = styled(Flex)({
  opacity: 0,
  pointerEvents: "none",
  cursor: "grab",
  alignItems: "center",
  lineHeight: 0,
  flexShrink: 0,

  [`${Row}:hover &, &:focus-visible`]: {
    opacity: 1,
    pointerEvents: "auto",
  },

  "@media (hover: none)": {
    opacity: 1,
    pointerEvents: "auto",
  },

  "&:active": {
    cursor: "grabbing",
  },
});

// Hidden with opacity only, so the buttons can still take keyboard focus, which reveals them
const ButtonWrapper = styled(Flex)({
  opacity: 0,
  gap: 8,
  flexShrink: 0,
  transition: "opacity 0.2s",

  [`${Row}:hover &, &:focus-within`]: {
    opacity: 1,
  },

  "@media (hover: none)": {
    opacity: 1,
  },
});
